// API 문서 파라미터 설명 셀을 렌더하는 컴포넌트
import MatrixTable from './MatrixTable';
import classes from './ParamDesc.module.css';

/**
 * 허용값 목록을 [값, 설명] 쌍 배열로 정규화한다.
 * 문자열 배열(['A', 'B'])과 쌍 배열([['A', '설명']]) 두 형태를 모두 받는다.
 *
 * 설명 유무(hasDescriptions)를 함께 돌려주는 이유는 레이아웃이 그 값으로 갈리기 때문이다.
 * 렌더 쪽에서 따로 판정하면 정규화와 기준이 어긋날 수 있다.
 * 첫 원소만 보는 판정은 문서 데이터에 두 형태가 섞인 지점이 없어 전체를 대표한다.
 *
 * @returns {{items: Array<[any, any]>, hasDescriptions: boolean}}
 */
function normalizeAllowedValues(allowedValues) {
  const hasDescriptions = Array.isArray(allowedValues[0]);
  return {
    items: allowedValues.map((item) => (hasDescriptions ? [item[0], item[1]] : [item, null])),
    hasDescriptions,
  };
}

/**
 * 파라미터 설명 셀.
 * 제목과 부가설명, 최대값·기본값 칩, 허용값 목록, 표, 주의사항 노트를 렌더한다.
 * table 슬롯은 허용값 목록으로 담을 수 없는 표를 위한 것이다. 헤더가 구조 라벨(Value/Description)이
 * 아니라 데이터인 표가 있어서, 허용값으로 옮기면 헤더 텍스트를 잃는다.
 * sub·허용값 설명·notes 는 문서 데이터가 HTML 문자열을 담고 있어 dangerouslySetInnerHTML 로 렌더한다.
 * notes 도 HTML 로 받는 이유는 원문 주의사항에 <code>·<strong> 이 섞여 있어서다.
 * 텍스트로 렌더하면 태그가 화면에 그대로 보인다.
 */
const ParamDesc = ({ title, sub, defaultValue, maxValue, allowedValues, notes, table }) => {
  const subItems = sub === undefined || sub === null ? [] : [].concat(sub);
  const noteItems = notes || [];
  const tableData = table && (table.headers?.length || table.rows?.length) ? table : null;
  const enumData =
    allowedValues && allowedValues.length > 0 ? normalizeAllowedValues(allowedValues) : null;

  return (
    <div className={classes.container}>
      <div className={classes.title}>{title}</div>

      {subItems.map((item, i) => (
        <div
          className={classes.sub}
          key={i}
          dangerouslySetInnerHTML={{ __html: item }}
        />
      ))}

      {/* 주의사항은 부가설명 바로 아래에 온다. 원문(`ul.style` + `li`)의 위치이고,
          이 목록은 경고가 아니라 설명의 연장이라 값 제약보다 앞이다 */}
      {noteItems.length > 0 && (
        <ul className={classes.notes}>
          {noteItems.map((note, i) => (
            <li
              className={classes.noteItem}
              key={i}
              dangerouslySetInnerHTML={{ __html: note }}
            />
          ))}
        </ul>
      )}

      {(maxValue !== undefined || defaultValue !== undefined) && (
        <div className={classes.chips}>
          {/* 최대값을 먼저 둔다. 입력 가능 범위를 보고 나서 미입력 시 동작을 보는 순서다 */}
          {maxValue !== undefined && (
            <div className={classes.chip}>
              최대값<span className={classes.chipValue}>{maxValue}</span>
            </div>
          )}
          {defaultValue !== undefined && (
            <div className={classes.chip}>
              기본값<span className={classes.chipValue}>{defaultValue}</span>
            </div>
          )}
        </div>
      )}

      {enumData && (
        <div className={classes.enum}>
          <div className={classes.enumLabel}>허용값</div>
          {/* 설명이 없으면 한 줄에 값 하나를 두는 셈이라 가로로 흘린다.
              설명이 있으면 값과 설명을 대조해야 해서 세로를 유지한다 */}
          <div
            className={
              enumData.hasDescriptions
                ? classes.enumList
                : `${classes.enumList} ${classes.enumListInline}`
            }
          >
            {enumData.items.map(([value, description], i) => (
              <div className={classes.enumItem} key={i}>
                <code className={classes.enumValue}>{value}</code>
                {description && (
                  <span
                    className={classes.enumDesc}
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tableData && <MatrixTable headers={tableData.headers} rows={tableData.rows} />}
    </div>
  );
};

export default ParamDesc;
